import { useState } from 'react';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { ParentAction, approveIdentification } from '../services/billingApi';
import { PaymentGroup } from '../services/paymentsFilter';
import SelectedInvoicesSummary, { payerLabel, runPerPayment } from './SelectedInvoicesSummary';

interface Props {
    /** as faturas selecionadas, por pagamento: uma aprovação por pagamento (AF-4139) */
    groups: PaymentGroup[];
    /**
     * A cobrança anterior escolhida na aprovação (AF-4062), só das faturas em que a aprovação
     * decide. As demais vão só pelo id: a escolha delas é a da marcação.
     */
    choices?: Record<string, ParentAction | ''>;
    onClose: () => void;
    onApproved: () => void;
}

const warningOf = (invoiceCount: number, paymentCount: number): string => {
    const settled = invoiceCount > 1 ? 'Estas faturas serão liquidadas' : 'Esta fatura será liquidada';
    const by = paymentCount > 1 ? 'estes pagamentos' : 'este pagamento';
    return `${settled} com ${by}. Caso precise desfazer essa ação, será necessário estornar.`;
};

const ApproveInvoicesDialog: React.FC<Props> = ({
    groups, choices = {}, onClose, onApproved,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const invoices = groups.flatMap(group => group.invoices);
    const several = invoices.length > 1;

    const itemsOf = (group: PaymentGroup) => group.invoices.map(inv => (
        inv.invoiceId in choices
            ? { invoice_id: inv.invoiceId, parent_action: choices[inv.invoiceId] || null }
            : inv.invoiceId
    ));

    const confirm = async () => {
        setSubmitting(true);
        const { done, failed } = await runPerPayment(
            groups, group => approveIdentification(group.payment.paymentId, itemsOf(group)));
        setSubmitting(false);
        failed.forEach(({ group, message }) => toast.error(
            `Não foi possível aprovar o recebimento de ${payerLabel(group.payment)}: ${message}`));
        if (done.length === 0) return;
        toast.success(failed.length === 0
            ? (several
                ? `Identificação aprovada. ${invoices.length} faturas foram liquidadas.`
                : 'Identificação aprovada. A fatura foi liquidada.')
            : `Identificação aprovada no recebimento de ${done.map(g => payerLabel(g.payment)).join(', ')}.`);
        onApproved();
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div role="dialog" aria-label="Aprovar identificação"
                className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <FaCheckCircle className="text-green-600" />
                        {several ? `Aprovar ${invoices.length} faturas` : 'Aprovar esta fatura'}
                    </h3>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 space-y-3">
                    <SelectedInvoicesSummary groups={groups} />
                    <p className="text-sm px-3 py-2 rounded bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        <strong>ATENÇÃO:</strong> {warningOf(invoices.length, groups.length)}
                    </p>
                </div>

                <div className="px-6 py-3 border-t dark:border-slate-600 flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={confirm} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
                        {submitting ? 'Aprovando...' : 'Aprovar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApproveInvoicesDialog;
